import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import {
  Card,
  Badge,
  Button,
  Modal,
  Input,
  Select,
  DashboardStatCard,
  ProgressBar,
  EmptyState,
  HelpTooltip,
} from "../../components/common/UI";
import { confirmAction } from "../../utils/sweetAlert";
import {
  ChartCard,
  DepartmentStatusDonut,
  ComplianceScoreGauge,
  DepartmentScoresBar,
} from "../../components/common/GapAssessmentCharts";
import { useAssessmentCycles } from "../../hooks/useAssessmentCycles";
import {
  Building2,
  Lock,
  Unlock,
  ClipboardCheck,
  Plus,
  Layers,
  ShieldCheck,
  Handshake,
  TrendingUp,
  CheckCircle2,
  Clock,
  CircleDashed,
  Crown,
  FileText,
  Target,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// Module theme — distinct icon + accent per module so PIA and TPRM read as
// two different products at a glance, not just a relabeled template.
// NOTE: if `Handshake` isn't available in your installed lucide-react
// version, swap it for `Building2` or `Network` — everything else in the
// tprm config still applies.
// ─────────────────────────────────────────────────────────────────────────
const MODULE_CONFIG = {
  pia: {
    label: "PIA",
    title: "PIA Assessment Tracking",
    subtitle:
      "Privacy Impact Assessment cycles, locks, and department progress",
    apiBase: "/pia",
    icon: ShieldCheck,
    headerGradient: "from-indigo-500 via-blue-500 to-blue-600",
    scoreGradient: "from-indigo-500 to-blue-600",
    softRing: "ring-indigo-600/15",
    softBorder: "border-indigo-200",
    softBg: "bg-indigo-50/60",
    badgeVariant: "primary",
    dot: "bg-indigo-500",
    dotLight: "bg-indigo-400",
  },
  tprm: {
    label: "TPRM",
    title: "TPRM Assessment Tracking",
    subtitle:
      "Third-party risk assessment cycles, locks, and department progress",
    apiBase: "/tprm",
    icon: Handshake,
    headerGradient: "from-amber-500 via-orange-500 to-orange-600",
    scoreGradient: "from-amber-500 to-orange-600",
    softRing: "ring-amber-600/15",
    softBorder: "border-amber-200",
    softBg: "bg-amber-50/60",
    badgeVariant: "warning",
    dot: "bg-amber-500",
    dotLight: "bg-amber-400",
  },
};

const STATUS_META = {
  submitted: { badge: "success", icon: CheckCircle2, label: "Submitted" },
  in_progress: { badge: "warning", icon: Clock, label: "In progress" },
  not_started: { badge: "default", icon: CircleDashed, label: "Not started" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.not_started;
  const Icon = meta.icon;
  return (
    <Badge variant={meta.badge}>
      <span className="inline-flex items-center gap-1">
        <Icon size={11} />
        {meta.label}
      </span>
    </Badge>
  );
}

function SectionHeading({
  icon: Icon,
  iconColor = "text-indigo-600",
  title,
  tooltip,
  className = "mb-4",
}) {
  return (
    <h3
      className={`font-semibold text-slate-800 flex items-center gap-2 ${className}`}
    >
      {Icon && <Icon size={18} className={iconColor} />}
      {title}
      {tooltip && <HelpTooltip position="bottom" content={tooltip} />}
    </h3>
  );
}

function StatGrid({ items, loading = false }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {items.map((s) => (
        <DashboardStatCard
          key={s.label}
          label={s.label}
          value={s.value}
          sub={s.sub}
          tooltip={s.tooltip}
          icon={s.icon}
          color={s.color || "blue"}
          loading={loading}
        />
      ))}
    </div>
  );
}

function SectionTable({ columns, rows, emptyTitle, emptyIcon }) {
  if (!rows?.length) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description="No records for this cycle yet."
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            {columns.map((col) => (
              <th key={col.key} className="py-2 pr-4 font-medium">
                {col.label}
                {col.tooltip && (
                  <HelpTooltip content={col.tooltip} position="bottom" />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.department_id || row.id}
              className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors"
            >
              {columns.map((col) => (
                <td key={col.key} className="py-2.5 pr-4 align-middle">
                  {col.render ? col.render(row) : (row[col.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ModuleAssessmentAdmin({ module = "pia" }) {
  const config = MODULE_CONFIG[module] || MODULE_CONFIG.pia;
  const Icon = config.icon;
  const { showNotification } = useApp();
  const {
    cycles,
    currentCycleId,
    refresh: refreshCycles,
  } = useAssessmentCycles(module);
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycleModal, setCycleModal] = useState(false);
  const [newCycle, setNewCycle] = useState({
    year: new Date().getFullYear() + 1,
    name: "",
    status: "open",
  });

  // Reset when switching PIA ↔ TPRM so a PIA cycle id is never sent to /tprm/...
  useEffect(() => {
    setSelectedCycleId("");
    setTracking(null);
    setLoading(true);
  }, [module]);

  const effectiveCycleId = useMemo(() => {
    if (!cycles.length) return "";
    if (selectedCycleId && cycles.some((c) => c.id === selectedCycleId)) {
      return selectedCycleId;
    }
    if (currentCycleId && cycles.some((c) => c.id === currentCycleId)) {
      return currentCycleId;
    }
    return cycles[0]?.id || "";
  }, [cycles, selectedCycleId, currentCycleId]);

  useEffect(() => {
    if (!effectiveCycleId) return;
    const selectedValid =
      selectedCycleId && cycles.some((c) => c.id === selectedCycleId);
    if (!selectedValid && effectiveCycleId !== selectedCycleId) {
      setSelectedCycleId(effectiveCycleId);
    }
  }, [effectiveCycleId, selectedCycleId, cycles]);

  const loadTracking = useCallback(async () => {
    if (!effectiveCycleId) {
      setTracking(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ cycle_id: effectiveCycleId });
      const res = await apiFetch(`${config.apiBase}/admin/tracking?${params}`);
      if (!res.ok)
        throw new Error((await res.json()).detail || "Failed to load tracking");
      setTracking(await res.json());
    } catch (e) {
      setTracking(null);
      showNotification(
        e.message || "Failed to load assessment tracking",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [effectiveCycleId, config.apiBase, showNotification]);

  useEffect(() => {
    loadTracking();
  }, [loadTracking]);

  const handleCreateCycle = async () => {
    try {
      const res = await apiFetch(`${config.apiBase}/cycles`, {
        method: "POST",
        body: JSON.stringify({
          year: Number(newCycle.year),
          name: newCycle.name || `${newCycle.year} Assessment`,
          status: newCycle.status,
          is_current: true,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification("Assessment cycle created", "success");
      setCycleModal(false);
      await refreshCycles();
      const data = await res.json();
      if (data.cycle?.id) setSelectedCycleId(data.cycle.id);
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const handleToggleCycleStatus = async (cycle) => {
    const nextStatus = cycle.status === "open" ? "closed" : "open";
    const action = nextStatus === "open" ? "reopen" : "close";
    const ok = await confirmAction({
      title: `${action === "reopen" ? "Reopen" : "Close"} cycle?`,
      text: `"${cycle.name}" will be ${nextStatus === "open" ? "reopened for submissions" : "closed to new submissions"}.`,
      confirmText: action === "reopen" ? "Reopen" : "Close",
      icon: "question",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`${config.apiBase}/cycles/${cycle.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification(
        `Cycle ${nextStatus === "open" ? "reopened" : "closed"}`,
        "success",
      );
      await refreshCycles();
      loadTracking();
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const handleSetCurrent = async (cycle) => {
    try {
      const res = await apiFetch(
        `${config.apiBase}/cycles/${cycle.id}/set-current`,
        {
          method: "POST",
        },
      );
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification("Current cycle updated", "success");
      await refreshCycles();
      setSelectedCycleId(cycle.id);
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const handleReopen = async (submissionId) => {
    const ok = await confirmAction({
      title: "Reopen assessment?",
      text: "This locked submission will be reopened for the user to edit.",
      confirmText: "Reopen",
      icon: "question",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(
        `${config.apiBase}/admin/submissions/${submissionId}/reopen`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification("Assessment reopened", "success");
      loadTracking();
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const summary = tracking?.summary || {};
  const assessments = tracking?.assessments || {};
  const departments = tracking?.departments || [];
  const submitted = summary.submitted ?? 0;
  const total = summary.departments_total ?? 0;
  const notStarted = summary.not_started ?? 0;
  const inProgress = summary.in_progress ?? 0;
  const completionPct =
    assessments.completion_pct ?? summary.completion_pct ?? (total ? Math.round((submitted / total) * 100) : 0);
  const avgScore = summary.avg_score ?? summary.average_score ?? null;

  // Loading state — mirrors Gap Assessment's loading screen, themed per module
  const loadingScreen = (
    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
      <div className="relative mb-6">
        <div
          className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${config.headerGradient} flex items-center justify-center shadow-lg`}
        >
          <Icon size={36} className="text-white" />
        </div>
        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow">
          <span
            className={`block w-3 h-3 rounded-full ${config.dot} animate-pulse`}
          />
        </span>
      </div>
      <h2 className="text-xl font-semibold text-slate-800 mb-1">
        Loading {config.label} Dashboard
      </h2>
      <p className="text-sm text-slate-500 mb-6">
        Fetching {config.label} tracking data for this cycle…
      </p>
      <div className="flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`block w-2 h-2 rounded-full ${config.dotLight} animate-bounce`}
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </div>
    </div>
  );

  // Matches the original branch order exactly: an empty cycle list only wins
  // once we're sure loading has actually finished, otherwise the loading
  // loading screen below covers the initial-fetch window too (cycles.length
  // is still 0 at that point, same as before this redesign).
  if (!cycles.length && !loading) {
    return (
      <div className="space-y-6 animate-fade-in p-1">
        <div
          data-tour-id={`tour-${module}-admin-header`}
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.headerGradient} flex items-center justify-center shadow-md flex-shrink-0`}
            >
              <Icon size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                {config.title}
                <Badge variant={config.badgeVariant} size="xs">
                  {config.label}
                </Badge>
              </h1>
              <p className="text-sm text-slate-500">{config.subtitle}</p>
            </div>
          </div>
          <Button
            data-tour-id={`tour-${module}-admin-new-cycle`}
            variant="secondary"
            onClick={() => setCycleModal(true)}
          >
            <Plus size={14} className="mr-1" /> New cycle
          </Button>
        </div>
        <div data-tour-id={`tour-${module}-admin-empty`}>
        <Card>
          <EmptyState
            icon={<Layers size={40} />}
            title="No assessment cycles"
            description={`Create a cycle to start tracking department submissions for ${config.label}.`}
            action={
              <Button onClick={() => setCycleModal(true)}>
                <Plus size={14} className="mr-1" /> New cycle
              </Button>
            }
          />
        </Card>
        </div>
        <Modal
          open={cycleModal}
          onClose={() => setCycleModal(false)}
          title={`Create ${config.label} assessment cycle`}
        >
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Year</label>
              <Input
                type="number"
                value={newCycle.year}
                onChange={(e) =>
                  setNewCycle((p) => ({ ...p, year: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input
                value={newCycle.name}
                placeholder={`${newCycle.year} ${config.label} Assessment`}
                onChange={(e) =>
                  setNewCycle((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCycleModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCycle}>Create cycle</Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  if (loading && !tracking) {
    return loadingScreen;
  }

  return (
    <div className="space-y-6 animate-fade-in p-1">
      <div
        data-tour-id={`tour-${module}-admin-header`}
        className="flex flex-wrap items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.headerGradient} flex items-center justify-center shadow-md flex-shrink-0`}
          >
            <Icon size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              {config.title}
              <Badge variant={config.badgeVariant} size="xs">
                {config.label}
              </Badge>
            </h1>
            <p className="text-sm text-slate-500">{config.subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div data-tour-id={`tour-${module}-admin-cycle`}>
            <Select
              value={effectiveCycleId}
              onChange={(e) => setSelectedCycleId(e.target.value)}
              className="min-w-[200px]"
            >
              {cycles.length === 0 ? (
                <option value="">No cycles yet</option>
              ) : (
                cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.status})
                  </option>
                ))
              )}
            </Select>
          </div>
          <Button
            data-tour-id={`tour-${module}-admin-new-cycle`}
            variant="secondary"
            onClick={() => setCycleModal(true)}
          >
            <Plus size={14} className="mr-1" /> New cycle
          </Button>
        </div>
      </div>

      <>
        <div data-tour-id={`tour-${module}-admin-tracking`}>
        <Card>
          <SectionHeading
            icon={ClipboardCheck}
            iconColor={module === "tprm" ? "text-amber-600" : "text-indigo-600"}
            title="Assessment Tracking"
            tooltip={`Counts of all ${config.label} records in this cycle — drafts, submitted, locked (final), and reopened.`}
          />
          <StatGrid
            loading={loading}
            items={[
              {
                label: "Total Assessments",
                value: assessments.total_assessments ?? 0,
                icon: FileText,
                color: "blue",
                tooltip: "All assessment records including drafts and submissions.",
              },
              {
                label: "Draft Assessments",
                value: assessments.draft_assessments ?? 0,
                icon: CircleDashed,
                color: "amber",
                tooltip: "Assessments started but not yet submitted.",
              },
              {
                label: "Submitted",
                value: assessments.submitted_assessments ?? 0,
                icon: ClipboardCheck,
                color: module === "tprm" ? "amber" : "indigo",
                tooltip: "Assessments formally submitted by users.",
              },
              {
                label: "Locked",
                value: assessments.locked_assessments ?? 0,
                icon: Lock,
                color: "blue",
                tooltip:
                  "Finalised submissions — read-only unless reopened by admin.",
              },
              {
                label: "Reopened",
                value: assessments.reopened_assessments ?? 0,
                icon: Unlock,
                color: "rose",
                tooltip:
                  "Previously locked assessments unlocked for editing again.",
              },
              {
                label: "Completion %",
                value: `${completionPct}%`,
                icon: Target,
                color: module === "tprm" ? "amber" : "indigo",
                tooltip:
                  "Percentage of departments with a completed (locked) assessment.",
              },
            ]}
          />
        </Card>
        </div>

        <div
          data-tour-id={`tour-${module}-admin-summary`}
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <DashboardStatCard
            label="Departments"
            value={total}
            icon={Building2}
            gradient="from-sky-500 to-blue-600"
            tooltip="Active departments in this tenant"
            loading={loading}
          />
          <DashboardStatCard
            label="Submitted"
            value={submitted}
            icon={ClipboardCheck}
            gradient="from-emerald-400 to-teal-600"
            tooltip={`Departments that locked a ${config.label} submission this cycle`}
            loading={loading}
          />
          <DashboardStatCard
            label="Not started"
            value={notStarted}
            icon={Layers}
            gradient="from-slate-400 to-slate-500"
            tooltip="Departments with no draft or submission yet"
            loading={loading}
          />
          <DashboardStatCard
            label={`Avg ${config.label} score`}
            value={avgScore != null ? `${avgScore}%` : "—"}
            icon={Icon}
            gradient={config.scoreGradient}
            tooltip={`Average score across submitted ${config.label} assessments this cycle`}
            loading={loading}
          />
        </div>

        <div
          data-tour-id={`tour-${module}-admin-charts`}
          className="grid grid-cols-1 lg:grid-cols-3 gap-4"
        >
          <ChartCard
            title="Completion status"
            tooltip="Departments grouped by submission status for the selected cycle."
          >
            <DepartmentStatusDonut
              departments={{
                completed: submitted,
                in_progress: inProgress,
                not_started: notStarted,
                total,
              }}
            />
          </ChartCard>
          <ChartCard
            title={`Overall ${config.label} score`}
            tooltip="Average score across locked submissions for the selected cycle."
          >
            <ComplianceScoreGauge score={avgScore} />
          </ChartCard>
          <ChartCard
            title="Department scores"
            tooltip="Score by department for the selected cycle."
          >
            <DepartmentScoresBar rows={departments} height={240} />
          </ChartCard>
        </div>

        <div data-tour-id={`tour-${module}-admin-completion`}>
        <Card className={submitted > 0 ? `border ${config.softBorder}` : ""}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp size={18} className="text-slate-500" />
              Cycle completion
            </h3>
            <Badge
              variant={
                tracking?.cycle?.status === "open" ? "success" : "default"
              }
            >
              {tracking?.cycle?.status || "open"}
            </Badge>
          </div>
          <ProgressBar
            value={completionPct}
            max={100}
            color={
              completionPct >= 75
                ? "emerald"
                : completionPct >= 40
                  ? "amber"
                  : "red"
            }
          />
          <p className="text-xs text-slate-500 mt-2">
            {submitted}/{total} departments submitted ({completionPct}%)
          </p>
        </Card>
        </div>

        <div data-tour-id={`tour-${module}-admin-departments`}>
        <Card>
          <h3 className="font-semibold text-slate-800 mb-4">
            Department status
          </h3>
          <SectionTable
            emptyTitle="No departments"
            emptyIcon={<Building2 size={36} />}
            rows={departments}
            columns={[
              { key: "department_name", label: "Department" },
              { key: "department_code", label: "Code" },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusPill status={r.status} />,
              },
              {
                key: "score",
                label: "Score",
                render: (r) => (r.score != null ? `${r.score}%` : "—"),
              },
              {
                key: "submitted_at",
                label: "Submitted",
                render: (r) =>
                  r.submitted_at
                    ? new Date(r.submitted_at).toLocaleString()
                    : "—",
              },
              {
                key: "actions",
                label: "Actions",
                render: (r) =>
                  r.is_locked && r.submission_id ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleReopen(r.submission_id)}
                    >
                      <Unlock size={12} className="mr-1" /> Reopen
                    </Button>
                  ) : r.is_locked ? (
                    <span className="text-slate-400 text-xs inline-flex items-center gap-1">
                      <Lock size={12} /> Locked
                    </span>
                  ) : (
                    "—"
                  ),
              },
            ]}
          />
        </Card>
        </div>

        <div data-tour-id={`tour-${module}-admin-cycles`}>
        <Card>
          <h3 className="font-semibold text-slate-800 mb-4">
            Assessment cycles
          </h3>
          <SectionTable
            emptyTitle="No cycles"
            emptyIcon={<Layers size={36} />}
            rows={cycles}
            columns={[
              { key: "year", label: "Year" },
              { key: "name", label: "Name" },
              {
                key: "status",
                label: "Status",
                render: (r) => (
                  <Badge variant={r.status === "open" ? "success" : "default"}>
                    {r.status}
                  </Badge>
                ),
              },
              {
                key: "is_current",
                label: "Current",
                render: (r) =>
                  r.is_current ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                      <Crown size={13} /> Current
                    </span>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  ),
              },
              {
                key: "actions",
                label: "Actions",
                render: (r) => (
                  <div className="flex flex-wrap gap-2">
                    {!r.is_current && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleSetCurrent(r)}
                      >
                        Set current
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={r.status === "open" ? "secondary" : "primary"}
                      onClick={() => handleToggleCycleStatus(r)}
                    >
                      {r.status === "open" ? "Close" : "Reopen"}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </Card>
        </div>
      </>

      <Modal
        open={cycleModal}
        onClose={() => setCycleModal(false)}
        title={`Create ${config.label} assessment cycle`}
      >
        <div className="space-y-4">
          <div
            className={`flex items-center gap-3 rounded-xl border ${config.softBorder} ${config.softBg} px-3 py-2.5`}
          >
            <div
              className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.headerGradient} flex items-center justify-center flex-shrink-0`}
            >
              <Icon size={16} className="text-white" />
            </div>
            <p className="text-xs text-slate-600">
              New cycles start <span className="font-semibold">open</span> and
              become the current {config.label} cycle once created.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Year</label>
            <Input
              type="number"
              value={newCycle.year}
              onChange={(e) =>
                setNewCycle((p) => ({ ...p, year: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Name</label>
            <Input
              value={newCycle.name}
              placeholder={`${newCycle.year} ${config.label} Assessment`}
              onChange={(e) =>
                setNewCycle((p) => ({ ...p, name: e.target.value }))
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCycleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCycle}>Create cycle</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
